/**
 * Drives a Google Cloud Storage resumable upload session from the browser.
 *
 * Lifecycle:
 *   1. `ResumableUploadSession.create({ fileName, mimeType })` calls our backend
 *      (`ApiService.uploadUrlsCreate` with type `INITIATE_RESUMABLE_UPLOAD`); the
 *      backend POSTs to GCS and returns the session URL plus an
 *      `asyncOperationUuid` that ties this upload into the downstream Celery
 *      pipeline (validation, transcription, asset creation).
 *   2. Callers stream blobs in via `appendBlob(blob)` as they arrive (e.g. from
 *      `MediaRecorder.dataavailable`). Internally we buffer to a 256 KiB-aligned
 *      boundary — GCS requires non-final chunks to be a multiple of 256 KiB —
 *      and PUT one chunk at a time to the session URL.
 *   3. `finalize()` flushes whatever is left as the final (size-known) chunk
 *      and resolves with `{ bucket, file_name, asyncOperationUuid }` which the
 *      caller hands to `UploadMediaController._finalizeUploadedAsset` to trigger
 *      the rest of the existing upload pipeline.
 *
 * Errors during a PUT abort the session — callers should treat a failed
 * resumable upload the same way they treat any other failed upload.
 */
import { ApiService, UploadUrlsRequestTypeEnum } from 'js/src/client';

const GCS_CHUNK_ALIGNMENT_BYTES = 256 * 1024;
const MAX_PUT_ATTEMPTS = 5;
const INITIAL_BACKOFF_MS = 200;
const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type ResumableUploadInit = {
    fileName: string;
    mimeType: string;
};

type ResumableUploadResult = {
    bucket: string;
    file_name: string;
    asyncOperationUuid: string;
};

export class ResumableUploadError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ResumableUploadError';
    }
}

export class ResumableUploadSession {
    readonly sessionUrl: string;
    readonly bucket: string;
    readonly fileName: string;
    readonly asyncOperationUuid: string;
    readonly mimeType: string;

    /** Bytes already accepted by GCS (i.e. successfully PUT). */
    #bytesUploaded = 0;

    /** Bytes buffered locally, not yet sent. */
    #pendingBuffer: Blob[] = [];
    #pendingBytes = 0;

    /** Promise chain that serializes PUTs — one in-flight at a time. */
    #drain: Promise<void> = Promise.resolve();

    /** Cleared once `finalize()` is called; further `appendBlob` calls throw. */
    #closed = false;

    /** Set if any PUT fails; subsequent operations short-circuit with this. */
    #failure: Error | null = null;

    private constructor(init: {
        sessionUrl: string;
        bucket: string;
        fileName: string;
        asyncOperationUuid: string;
        mimeType: string;
    }) {
        this.sessionUrl = init.sessionUrl;
        this.bucket = init.bucket;
        this.fileName = init.fileName;
        this.asyncOperationUuid = init.asyncOperationUuid;
        this.mimeType = init.mimeType;
    }

    static async create({
        fileName,
        mimeType,
    }: ResumableUploadInit): Promise<ResumableUploadSession> {
        const response = await ApiService.uploadUrlsCreate({
            body: {
                file_name: fileName,
                type: UploadUrlsRequestTypeEnum.INITIATE_RESUMABLE_UPLOAD,
            },
        });
        return new ResumableUploadSession({
            sessionUrl: response.data.upload_url,
            bucket: response.data.bucket,
            fileName: response.data.file_name,
            asyncOperationUuid: response.data.async_operation_uuid,
            mimeType,
        });
    }

    get bytesUploaded(): number {
        return this.#bytesUploaded;
    }

    /**
     * Queue a blob for upload. Returns immediately. If enough has accumulated
     * to form a 256 KiB-aligned chunk, the next PUT is kicked off (serialized
     * after any in-flight PUT). Errors surface via `finalize()` (which re-
     * throws `#failure`), not from this method — `appendBlob` is invoked from
     * a MediaRecorder event listener, where an uncaught throw becomes browser
     * console noise. Once the session is closed or has failed, further blobs
     * are silently dropped.
     */
    appendBlob(blob: Blob): void {
        if (this.#closed || this.#failure || blob.size === 0) {
            return;
        }
        this.#pendingBuffer.push(blob);
        this.#pendingBytes += blob.size;

        if (this.#pendingBytes >= GCS_CHUNK_ALIGNMENT_BYTES) {
            this.#drain = this.#drain.then(() => this.#flushIntermediate());
            // Swallow rejection here — `finalize()` re-throws via `#failure`.
            this.#drain.catch(() => {});
        }
    }

    /**
     * Send all buffered bytes that we have, in 256 KiB-aligned chunks, leaving
     * the tail (smaller than 256 KiB) buffered for the final flush.
     */
    async #flushIntermediate(): Promise<void> {
        if (this.#failure) return;
        const sendable =
            Math.floor(this.#pendingBytes / GCS_CHUNK_ALIGNMENT_BYTES) * GCS_CHUNK_ALIGNMENT_BYTES;
        if (sendable === 0) return;
        const chunk = this.#takeFromBuffer(sendable);
        await this.#putChunk(chunk, /* isFinal */ false, /* total */ null);
    }

    /**
     * Take exactly `bytes` bytes from the head of the pending buffer, slicing
     * the last source blob if needed and putting the remainder back.
     */
    #takeFromBuffer(bytes: number): Blob {
        const taken: BlobPart[] = [];
        let remaining = bytes;
        while (remaining > 0 && this.#pendingBuffer.length > 0) {
            const next = this.#pendingBuffer[0];
            if (next.size <= remaining) {
                taken.push(next);
                remaining -= next.size;
                this.#pendingBuffer.shift();
            } else {
                taken.push(next.slice(0, remaining));
                this.#pendingBuffer[0] = next.slice(remaining);
                remaining = 0;
            }
        }
        this.#pendingBytes -= bytes;
        return new Blob(taken, { type: this.mimeType });
    }

    /**
     * Send a chunk to GCS, retrying transient failures with exponential
     * backoff. Between attempts we ask GCS how many bytes it actually has
     * (see `#queryUploadStatus`) and slice the chunk so we never re-upload
     * bytes the server already accepted.
     */
    async #putChunk(chunk: Blob, isFinal: boolean, total: number | null): Promise<void> {
        let chunkBytes = chunk;
        let lastError: ResumableUploadError | null = null;
        let backoffMs = INITIAL_BACKOFF_MS;

        for (let attempt = 1; attempt <= MAX_PUT_ATTEMPTS; attempt++) {
            const start = this.#bytesUploaded;
            const end = start + chunkBytes.size - 1;
            const totalSegment = isFinal && total != null ? String(total) : '*';
            const contentRange = `bytes ${start}-${end}/${totalSegment}`;

            let response: Response | null = null;
            try {
                response = await fetch(this.sessionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Range': contentRange },
                    body: chunkBytes,
                });
            } catch (e) {
                lastError = new ResumableUploadError(
                    `Network error during resumable upload PUT (range ${contentRange}): ${
                        (e as Error)?.message ?? 'unknown'
                    }`,
                );
            }

            if (response) {
                // 308 Resume Incomplete: chunk accepted, more chunks needed.
                // 200 / 201: final chunk accepted, object finalized.
                if (response.status === 308 || response.status === 200 || response.status === 201) {
                    this.#bytesUploaded += chunkBytes.size;
                    return;
                }
                lastError = new ResumableUploadError(
                    `Resumable upload PUT failed with status ${response.status} (range ${contentRange})`,
                );
                if (!RETRYABLE_STATUSES.has(response.status)) {
                    break; // permanent — don't bother retrying
                }
            }

            if (attempt >= MAX_PUT_ATTEMPTS) break;

            await sleep(backoffMs);
            backoffMs *= 2;

            // Ask GCS what it actually has. If it received part of our chunk
            // before the failure, slice off the prefix so we don't re-send it.
            const serverBytes = await this.#queryUploadStatus(isFinal ? total : null);
            if (serverBytes !== null) {
                const localStart = this.#bytesUploaded;
                const expectedEnd = localStart + chunkBytes.size;
                if (serverBytes >= expectedEnd) {
                    // GCS already has everything we were trying to send.
                    this.#bytesUploaded = serverBytes;
                    return;
                }
                if (serverBytes > localStart) {
                    chunkBytes = chunkBytes.slice(serverBytes - localStart);
                    this.#bytesUploaded = serverBytes;
                }
            }
        }

        this.#failure =
            lastError ?? new ResumableUploadError('Resumable upload failed after retries');
        throw this.#failure;
    }

    /**
     * Query the resumable session for how many bytes GCS has actually
     * received. Returns the byte count, or null if the status couldn't be
     * determined (in which case the caller should retry the whole chunk).
     */
    async #queryUploadStatus(total: number | null): Promise<number | null> {
        const totalSegment = total != null ? String(total) : '*';
        let response: Response;
        try {
            response = await fetch(this.sessionUrl, {
                method: 'PUT',
                headers: { 'Content-Range': `bytes */${totalSegment}` },
            });
        } catch {
            return null;
        }
        if (response.status === 308) {
            // Range header tells us what bytes the server has: "bytes=0-N"
            // (inclusive); no Range header means GCS received nothing.
            const rangeHeader = response.headers.get('Range');
            if (!rangeHeader) return 0;
            const match = rangeHeader.match(/bytes=0-(\d+)/);
            return match ? Number(match[1]) + 1 : 0;
        }
        if (response.status === 200 || response.status === 201) {
            // Upload was already finalized — GCS has the whole object. Only
            // meaningful in finalize() (where `total` is known); a 200/201 in
            // response to a non-final `bytes */*` query shouldn't happen and
            // we fall through to the null return so the retry loop fails.
            return total;
        }
        return null;
    }

    /**
     * Flushes the remaining buffer as the final (size-known) chunk and resolves
     * with the GCS location and asyncOperationUuid. Idempotent across rejected
     * sessions only by way of `#failure` — callers should not retry by re-calling.
     */
    async finalize(): Promise<ResumableUploadResult> {
        if (this.#closed) {
            throw new ResumableUploadError('finalize() called twice on the same session');
        }
        this.#closed = true;

        // Wait for any in-flight intermediate flush to complete.
        try {
            await this.#drain;
        } catch (e) {
            throw this.#failure ?? (e as Error);
        }
        if (this.#failure) throw this.#failure;

        const total = this.#bytesUploaded + this.#pendingBytes;
        if (total === 0) {
            throw new ResumableUploadError('finalize() called with no bytes uploaded');
        }
        if (this.#pendingBytes === 0) {
            // An intermediate flush already drained the buffer to a 256 KiB
            // boundary that matched the recording's total size, so there is
            // nothing left to PUT. Tell GCS the total via an empty-body PUT
            // so it can mark the object complete.
            await this.#finalizeWithoutBody(total);
        } else {
            const tail = this.#takeFromBuffer(this.#pendingBytes);
            await this.#putChunk(tail, /* isFinal */ true, total);
        }

        return {
            bucket: this.bucket,
            file_name: this.fileName,
            asyncOperationUuid: this.asyncOperationUuid,
        };
    }

    /**
     * Finalize when all bytes are already in GCS — sends an empty-body PUT
     * with a `bytes (star)/total` Content-Range. A 200/201 means GCS finalized
     * the object; anything else means GCS is missing bytes we believed we
     * sent, which we treat as a hard failure (no body left to re-send).
     */
    async #finalizeWithoutBody(total: number): Promise<void> {
        let lastError: ResumableUploadError | null = null;
        let backoffMs = INITIAL_BACKOFF_MS;

        for (let attempt = 1; attempt <= MAX_PUT_ATTEMPTS; attempt++) {
            let response: Response | null = null;
            try {
                response = await fetch(this.sessionUrl, {
                    method: 'PUT',
                    headers: { 'Content-Range': `bytes */${total}` },
                });
            } catch (e) {
                lastError = new ResumableUploadError(
                    `Network error finalizing resumable upload (total ${total}): ${
                        (e as Error)?.message ?? 'unknown'
                    }`,
                );
            }

            if (response) {
                if (response.status === 200 || response.status === 201) {
                    return;
                }
                lastError = new ResumableUploadError(
                    `Resumable upload finalize failed with status ${response.status} (total ${total})`,
                );
                if (!RETRYABLE_STATUSES.has(response.status)) {
                    break;
                }
            }

            if (attempt >= MAX_PUT_ATTEMPTS) break;
            await sleep(backoffMs);
            backoffMs *= 2;
        }

        this.#failure =
            lastError ?? new ResumableUploadError('Resumable upload finalize failed after retries');
        throw this.#failure;
    }
}
